import type { ShareBlocks, UnparsedShareBlocks } from '@common/types/site.type';
import {
    type DenormalizedBlockEntity,
    type DenormalizedTabEntity,
    type DenormalizedGridEntity,
    type WebLegacyWidgetEntity,
    type NormalizedSpecificSectionEntity,
    DenormalizedSectionEntity,
} from '@web/types';

import { inject, Injectable } from '@angular/core';

import { isWidgetEntityType } from '@common/helpers/widget-envelope';
import { WebWidgetLoadFillsService } from '@web/core/services/web-widget-load-fills.service';

import { PageStructureFacade } from './page-structure.facade';
import type { UnmigratedBlock } from './unmigrated-blocks';
import { restoreUnmigratedBlocks } from './unmigrated-blocks';

@Injectable({
    providedIn: 'root',
})
export class PageStructureService {
    private readonly structureFacade: PageStructureFacade = inject(PageStructureFacade);
    private readonly widgetLoadFills: WebWidgetLoadFillsService = inject(WebWidgetLoadFillsService);

    public setStructure(structure: DenormalizedBlockEntity[]): void {
        this.structureFacade.setPageStructure(structure);
    }

    public normalizeStructure(
        structure: DenormalizedBlockEntity[],
        sharedBlocks: ShareBlocks
    ): DenormalizedBlockEntity[] {
        if (!sharedBlocks) {
            return structure;
        }

        const normalizedStructure = [...structure];
        if (sharedBlocks?.header) {
            const header = sharedBlocks.header as DenormalizedBlockEntity;
            normalizedStructure.unshift(header);
        }

        if (sharedBlocks?.footer) {
            const footer = sharedBlocks.footer as DenormalizedBlockEntity;
            normalizedStructure.push(footer);
        }

        return normalizedStructure;
    }

    public prepareStructure(
        structure: DenormalizedBlockEntity[],
        sharedBlocks: ShareBlocks = null,
        unmigratedBlocks: readonly UnmigratedBlock[] = []
    ): { preparedStructure: DenormalizedBlockEntity[]; preparedSharedBlocks: ShareBlocks } {
        if (!sharedBlocks) {
            return {
                preparedStructure: this.widgetLoadFills.stripFills(
                    restoreUnmigratedBlocks(structure, unmigratedBlocks)
                ),
                preparedSharedBlocks: null,
            };
        }

        const preparedSharedBlocks = { footer: null, header: null, ...sharedBlocks };
        const preparedStructure = structure.reduce(
            (result: DenormalizedBlockEntity[], block: DenormalizedBlockEntity) => {
                if (!block.meta.isShared) {
                    result.push(block);
                    return result;
                }

                if (block.meta.role in preparedSharedBlocks) {
                    preparedSharedBlocks[block.meta.role] = block;
                }

                return result;
            },
            []
        );

        return {
            preparedStructure: this.widgetLoadFills.stripFills(
                restoreUnmigratedBlocks(preparedStructure, unmigratedBlocks)
            ),
            preparedSharedBlocks: this.widgetLoadFills.stripFills(preparedSharedBlocks),
        };
    }

    public mapBlockEntitiesToSharedBlocks(
        sharedBlocks: UnparsedShareBlocks | ShareBlocks,
        blocks: DenormalizedBlockEntity[]
    ): UnparsedShareBlocks | ShareBlocks {
        const sharedBlocksCopy = { ...sharedBlocks };
        if (typeof sharedBlocksCopy?.header === 'string') {
            sharedBlocksCopy.header = blocks.find((block) => block.id === sharedBlocksCopy.header);
        } else if ((sharedBlocksCopy?.header as DenormalizedBlockEntity)?.id) {
            const headerId = (sharedBlocksCopy.header as DenormalizedBlockEntity).id;
            sharedBlocksCopy.header = blocks.find((block) => block.id === headerId) || null;
        }

        if (typeof sharedBlocksCopy?.footer === 'string') {
            sharedBlocksCopy.footer = blocks.find((block) => block.id === sharedBlocksCopy.footer);
        } else if ((sharedBlocksCopy?.footer as DenormalizedBlockEntity)?.id) {
            const footerId = (sharedBlocksCopy.footer as DenormalizedBlockEntity).id;
            sharedBlocksCopy.footer = blocks.find((block) => block.id === footerId) || null;
        }

        return sharedBlocksCopy;
    }

    /**
     * The adapter calls this before the section is migrated, so it sees both
     * shapes: a page that has never been re-saved still carries the legacy
     * `type` tag, a migrated one carries `entitySubType`.
     */
    public static isSpecificSection(entity: object): entity is NormalizedSpecificSectionEntity {
        if ('entitySubType' in entity && entity.entitySubType === 'specific') {
            return true;
        }

        return 'type' in entity && entity.type === 'specific';
    }

    public static isEntityGrid(entity: unknown): entity is DenormalizedGridEntity {
        if (typeof entity !== 'object' || entity === null) {
            return false;
        }
        if ('entityType' in entity && entity.entityType === 'grid') {
            return true;
        }

        if ('widgets' in entity) {
            return true;
        }

        return false;
    }

    public static isEntityTab(entity: unknown): entity is DenormalizedTabEntity {
        if (typeof entity !== 'object' || entity === null) {
            return false;
        }

        if ('entityType' in entity && entity.entityType === 'tab') {
            return true;
        }

        return false;
    }

    public static isEntityWidget(entity: unknown): entity is WebLegacyWidgetEntity {
        if (typeof entity !== 'object' || entity === null) {
            return false;
        }

        // A versioned widget carries an envelope `entityType` too — unlike grids and tabs, it is a widget.
        if ('entityType' in entity) {
            return isWidgetEntityType(entity.entityType);
        }

        if ('widgets' in entity) {
            return false;
        }

        if ('children' in entity) {
            return false;
        }

        return true; // add some checks
    }
}
